<template>
  <section class="chart-card">
    <div class="card-header">
      <h3 class="card-title">
        业务办理增长趋势
      </h3>
      <div class="analyze-controls">
        <el-radio-group
          v-model="analyzeType"
          class="custom-radio-group"
        >
          <el-radio-button label="year">
            按年统计
          </el-radio-button>
          <el-radio-button label="quarter">
            按季统计
          </el-radio-button>
          <el-radio-button label="month">
            按月统计
          </el-radio-button>
        </el-radio-group>
        <div
          class="analyze-year-row"
          :class="{ 'is-placeholder': !(analyzeType === 'quarter' || analyzeType === 'month') }"
        >
          <el-date-picker
            v-model="analyzeYear"
            type="year"
            placeholder="选择年份"
            value-format="YYYY"
            class="analyze-year-picker"
            :clearable="false"
            :disabled-date="disabledAnalyzeYearDate"
            teleported
          />
        </div>
      </div>
    </div>
    <div
      ref="chartRef"
      class="chart-box"
      :class="`is-${chartMode}`"
    />
  </section>
</template>

<script setup>
import { markRaw, nextTick, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import * as echarts from 'echarts'
import { getBusinessHandlingGrowthTrend } from '@/api/normalChannelStatistics'

const SERIES_ORIGINAL = '原创数字备案'
const SERIES_PRODUCT = '产品发布存证'
/** 与业务办理统计饼图主色一致 */
const COLOR_ORIGINAL = '#FFB91F'
const COLOR_PRODUCT = '#A74E24'

const analyzeType = shallowRef('month')
const analyzeYear = shallowRef(String(new Date().getFullYear()))
const chartMode = shallowRef('desktop')
const chartRef = useTemplateRef('chartRef')

let chart = null
let resizeObserver = null
let requestSequence = 0
let chartData = {
  xAxisData: [],
  originalData: [],
  productData: [],
  isEmpty: true
}

function disabledAnalyzeYearDate(time) {
  return time.getFullYear() > new Date().getFullYear()
}

function resolveChartMode() {
  const width = chartRef.value?.clientWidth || window.innerWidth || 0
  if (width <= 520 || window.innerWidth <= 768) return 'mobile'
  if (width <= 900 || window.innerWidth <= 1024) return 'compact'
  return 'desktop'
}

function buildTimeAxisLabels(type, data) {
  const list = data || []
  if (type === 'year') {
    const years = list
      .map(item => parseInt(String(item.timeLabel || '').replace('年', ''), 10))
      .filter(year => Number.isFinite(year))
      .sort((a, b) => a - b)
    if (years.length > 0) {
      const labels = []
      for (let year = years[0]; year <= years[years.length - 1]; year++) {
        labels.push(`${year}年`)
      }
      return labels
    }
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 5 }, (_, index) => `${currentYear - 4 + index}年`)
  }
  if (type === 'quarter') {
    return ['第一季度', '第二季度', '第三季度', '第四季度']
  }
  if (type === 'month') {
    return Array.from({ length: 12 }, (_, index) => `${index + 1}月`)
  }
  return list.map(item => item.timeLabel)
}

function buildSeriesByLabel(labels, data, field) {
  const list = data || []
  return labels.map((label) => {
    const item = list.find(current => current.timeLabel === label)
    return item ? (item[field] || 0) : 0
  })
}

function buildYAxisOption(isEmpty) {
  const base = {
    type: 'value',
    minInterval: 1,
    axisLabel: {
      color: '#999',
      formatter: value => `${Number.isFinite(+value) ? Math.trunc(+value) : value}件`
    },
    splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
    axisLine: { show: false }
  }
  return isEmpty
    ? { ...base, min: 0, max: 1, splitNumber: 1 }
    : { ...base, min: null, max: null, splitNumber: null }
}

function buildAxisLabelOption(mode) {
  const isMonth = analyzeType.value === 'month'
  if (mode === 'mobile') {
    return { color: '#666', margin: 10, interval: isMonth ? 1 : 0, rotate: isMonth ? 35 : 0, fontSize: 11 }
  }
  if (mode === 'compact') {
    return { color: '#666', margin: 10, interval: 0, rotate: isMonth ? 30 : 0, fontSize: 12 }
  }
  return { color: '#666', margin: 12, interval: 0, rotate: 0, fontSize: 12 }
}

function buildGridOption(mode) {
  if (mode === 'mobile') {
    return { left: '2%', right: '3%', bottom: '18%', top: '12%', containLabel: true }
  }
  if (mode === 'compact') {
    return { left: '2%', right: '2%', bottom: '16%', top: '10%', containLabel: true }
  }
  return { left: '2%', right: '2%', bottom: '14%', top: '10%', containLabel: true }
}

function renderChart() {
  if (!chart) return
  chartMode.value = resolveChartMode()
  const mode = chartMode.value
  const barMaxWidth = mode === 'mobile' ? 22 : mode === 'compact' ? 28 : 36
  chart.setOption({
    color: [COLOR_ORIGINAL, COLOR_PRODUCT],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      confine: true
    },
    legend: {
      bottom: 0,
      itemWidth: 10,
      itemHeight: 10,
      icon: 'circle',
      data: [SERIES_ORIGINAL, SERIES_PRODUCT]
    },
    grid: buildGridOption(mode),
    xAxis: {
      type: 'category',
      data: chartData.xAxisData,
      axisLine: { lineStyle: { color: '#ddd' } },
      axisTick: { show: false },
      axisLabel: buildAxisLabelOption(mode)
    },
    yAxis: buildYAxisOption(chartData.isEmpty),
    series: [
      {
        name: SERIES_ORIGINAL,
        type: 'bar',
        barMaxWidth,
        itemStyle: { color: COLOR_ORIGINAL, borderRadius: [4, 4, 0, 0] },
        data: chartData.originalData
      },
      {
        name: SERIES_PRODUCT,
        type: 'bar',
        barGap: '20%',
        barMaxWidth,
        itemStyle: { color: COLOR_PRODUCT, borderRadius: [4, 4, 0, 0] },
        data: chartData.productData
      }
    ]
  }, { replaceMerge: ['series'] })
}

function setChartData(data) {
  const xAxisData = buildTimeAxisLabels(analyzeType.value, data)
  const originalData = buildSeriesByLabel(xAxisData, data, 'originalTotal')
  const productData = buildSeriesByLabel(xAxisData, data, 'productTotal')
  chartData = {
    xAxisData,
    originalData,
    productData,
    isEmpty: originalData.every(value => !value) && productData.every(value => !value)
  }
  renderChart()
}

async function loadGrowthTrend() {
  if (!chart) return
  const sequence = ++requestSequence
  const needYear = analyzeType.value === 'quarter' || analyzeType.value === 'month'
  if (needYear && !analyzeYear.value) {
    analyzeYear.value = String(new Date().getFullYear())
  }
  try {
    const response = await getBusinessHandlingGrowthTrend({
      analyzeType: analyzeType.value,
      ...(needYear ? { analyzeYear: analyzeYear.value } : {})
    })
    if (sequence !== requestSequence) return
    setChartData(response && response.code === 200 ? (response.data || []) : [])
  } catch (error) {
    if (sequence !== requestSequence) return
    console.error('查询业务办理增长趋势失败:', error)
    setChartData([])
  }
}

watch(analyzeType, (type) => {
  if (type === 'quarter' || type === 'month') {
    const currentYear = new Date().getFullYear()
    const year = parseInt(analyzeYear.value, 10)
    if (!Number.isFinite(year) || year > currentYear) {
      analyzeYear.value = String(currentYear)
    }
  }
  loadGrowthTrend()
})

watch(analyzeYear, () => {
  if (analyzeType.value === 'quarter' || analyzeType.value === 'month') {
    loadGrowthTrend()
  }
})

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  setChartData([])
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      chart?.resize()
      renderChart()
    })
    resizeObserver.observe(chartRef.value)
  }
  loadGrowthTrend()
})

onBeforeUnmount(() => {
  requestSequence++
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.chart-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 20px 24px 16px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.card-title {
  min-width: 0;
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
}

.analyze-controls {
  display: flex;
  width: auto;
  min-width: 0;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.analyze-year-row {
  display: flex;
  width: 100%;
  height: 38px;

  &.is-placeholder {
    visibility: hidden;
    pointer-events: none;
  }

  :deep(.el-date-editor) {
    box-sizing: border-box;
    width: 100% !important;
  }
}

.analyze-year-picker {
  width: 100% !important;
}

.custom-radio-group {
  --el-radio-button-checked-bg-color: rgba(13, 98, 243, 0.1);
  --el-radio-button-checked-border-color: #0d62f3;
  --el-radio-button-checked-text-color: #0d62f3;

  :deep(.el-radio-button__inner) {
    padding: 8px 16px;
  }

  :deep(.el-radio-button.is-active .el-radio-button__inner) {
    border: 1px solid #0d62f3;
    background: rgba(13, 98, 243, 0.1);
    box-shadow: none;
    color: #0d62f3;
  }
}

.chart-box {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 300px;

  &.is-compact {
    height: 340px;
  }

  &.is-mobile {
    height: 320px;
  }
}

@media (max-width: 768px) {
  .chart-card {
    padding: 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .analyze-controls,
  .custom-radio-group {
    width: 100%;
    align-items: stretch;
  }

  .custom-radio-group {
    :deep(.el-radio-button) {
      flex: 1 1 0;
    }

    :deep(.el-radio-button__inner) {
      width: 100%;
      padding: 8px 6px;
    }
  }
}

@media (max-width: 480px) {
  .chart-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .chart-box {
    height: 300px;
  }
}
</style>
